<template>
  <!-- A constellation's reveal, over whatever screen the person is on (quest
       journey 2026-10-27): its own animation; the light flies to the bar
       (the bar makes room, the tab is born or changes its face); then the
       hero, whose "Get started" lands inside the new room. -->
  <div class="qrv-root" :class="{ rm: isReduced }" data-testid="quest-reveal">
    <div v-if="stage === 'art'" ref="artEl" :class="['qrv', `qrv-${reveal.key}`, { out: fading }]" data-testid="quest-reveal-art" aria-hidden="true">
      <div class="qrv-fig" v-html="art"></div>
      <div class="qrv-cap" :style="{ '--cd': capDelay }">{{ sectionName }}</div>
      <!-- The Moon fills, and the first horoscope arrives with it. -->
      <div v-if="reveal.key === 'mo'" class="qrv-horo">
        <div class="qrv-eyebrow">Your horoscope · this morning</div>
        <p>{{ horoscopeLine }}</p>
      </div>
    </div>
    <span v-for="o in orbs" :key="o.id" ref="orbEls" class="qrv-orb" :class="{ echo: o.echo }" aria-hidden="true"></span>

    <div v-if="stage === 'hero'" class="qrv-over" :class="{ solo: reveal.heroOnly }" data-testid="quest-hero-wrap">
      <div class="qrv-hero" role="dialog" :aria-label="hero.title" data-testid="quest-hero">
        <button class="qrv-x" aria-label="Close" data-testid="quest-hero-close" @click="close">&times;</button>
        <div class="qrv-art">
          <img v-if="hero.art === 'portrait' && hero.portrait" :src="hero.portrait" alt="" class="qrv-por round" />
          <img v-else-if="hero.art === 'square' && hero.portrait" :src="hero.portrait" alt="" class="qrv-por square" />
          <svg v-else width="64" height="64" viewBox="0 0 32 32" aria-hidden="true" v-html="glyph"></svg>
        </div>
        <div class="qrv-eyebrow">{{ hero.eyebrow }}</div>
        <h3 class="qrv-title">{{ hero.title }}</h3>
        <p v-for="(para, i) in heroBody" :key="i" class="qrv-p">
          <template v-for="(seg, j) in para" :key="j"><b v-if="seg.b">{{ seg.text }}</b><template v-else>{{ seg.text }}</template></template>
        </p>
        <p v-if="hero.quiet" class="qrv-p qrv-quiet">{{ hero.quiet }}</p>
        <!-- The Zodiac Wheel: money shows for the first time, as one welcome. -->
        <div v-if="savedTotal" class="qrv-welcome" :class="{ claimed }" data-testid="quest-welcome-claim">
          <template v-if="!claimed">
            <div class="qrv-eyebrow">Your stars have been saving for you</div>
            <div class="qrv-amt">{{ shownAmount }} ✦</div>
            <button class="qrv-btn qrv-claim" :disabled="claiming" data-testid="quest-welcome-claim-button" @click="claim">{{ claiming ? '…' : 'Claim' }}</button>
          </template>
          <div v-else class="qrv-done">{{ savedTotal }} ✦ is yours. Spend it in the Community, or save it for later.</div>
        </div>
        <!-- The Sun: the Studio's price as a goal, never a shortfall. -->
        <div v-if="goal" class="qrv-goal" data-testid="quest-studio-goal">
          <div class="qrv-goal-bar" aria-hidden="true"><span :style="{ width: goal.pct + '%' }"></span></div>
          <p class="qrv-goal-line">{{ goal.line }}</p>
        </div>
        <button class="qrv-btn" data-testid="quest-get-started" @click="getStarted">Get started</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { store } from '@/store'
import { claimAllQuests } from '@/api-service'
import { SEC, miniGlyph, momentSegments } from '@/utils/quest-sky'
import { revealArt } from '@/utils/quest-reveal-art'
import { REVEALS, heroFor, landingFor, revealTiming } from '@/utils/quest-reveals'
import { seatGoalLine } from '@/utils/studio-unlock'

const props = defineProps({
  // { key: 'fs'…'su', seen, hero, heroOnly, saved, … } (quest-reveals.js)
  reveal: { type: Object, required: true },
  // What the art and the hero show: name, portrait, signs, other, people,
  // from, lesson_from, mystic { name, label, portrait }, sunSign, horoscope.
  data: { type: Object, default: () => ({}) },
  reduced: { type: Boolean, default: null },
})
const emit = defineEmits(['phase', 'seen', 'land', 'close', 'claimed'])

const stage = ref(props.reveal.heroOnly ? 'hero' : 'art')
const fading = ref(false)
const orbs = ref([])
const orbEls = ref([])
const artEl = ref(null)
const claimed = ref(false)
const claiming = ref(false)
const shownAmount = ref(0)
let timers = []
let anims = []
const later = (ms, fn) => { timers.push(setTimeout(fn, ms)) }

function prefersReduced () {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) } catch (e) { return false }
}
const isReduced = computed(() => (props.reduced == null ? prefersReduced() : props.reduced))
const sectionName = computed(() => (SEC[props.reveal.key] || {}).name || '')
const capDelay = computed(() => {
  const t = { fs: 1.8, ri: 1, ve: 3.4, zw: 3.8, mo: 4, su: 4.4 }[props.reveal.key] || 2
  return `${t}s`
})
const art = computed(() => revealArt(props.reveal.key, { ...props.data }))
const horoscopeLine = computed(() => props.data.horoscope || 'Read from your own chart, new each morning.')
// The Sun's Mystic is their own (drawn from their chart), never the bar's Mystic AIda.
const heroMystic = computed(() => (props.reveal.key === 'su' ? props.reveal.mystic : (props.data.mystic || props.reveal.mystic)))
const hero = computed(() => heroFor({ ...props.data, ...props.reveal, mystic: heroMystic.value }) || { title: '', body: [] })
const heroBody = computed(() => (hero.value.body || []).map((t) => momentSegments(t)))
const glyph = computed(() => miniGlyph(hero.value.art === 'portrait' ? 'ri' : hero.value.art === 'square' ? 'su' : hero.value.art, true))
const goal = computed(() => {
  const st = props.reveal.key === 'su' && props.data.studio
  if (!st || st.enough || !st.price) return null
  return { line: seatGoalLine(st.balance, st.price), pct: Math.min(100, Math.round((Number(st.balance) || 0) / st.price * 100)) }
})
const savedTotal = computed(() => (props.reveal.key === 'zw' && props.reveal.saved && Number(props.reveal.saved.total) > 0 ? Number(props.reveal.saved.total) : 0))
shownAmount.value = savedTotal.value

function barTarget () {
  const spec = REVEALS[props.reveal.key]
  if (!spec) return null
  const tab = document.querySelector(`[data-bar-slot="${spec.to}"]`)
  return tab ? (tab.querySelector('.quest-icon-wrap, .bar-portrait-wrap') || tab) : null
}

/** The light: a curve from the art's origin to the tab, three echoes behind it. */
function fly (done) {
  const from = artEl.value && artEl.value.querySelector('[data-origin]')
  const to = barTarget()
  if (!from || !to || typeof Element.prototype.animate !== 'function') { done(); return }
  const a = from.getBoundingClientRect(); const b = to.getBoundingClientRect()
  const x0 = a.left + a.width / 2; const y0 = a.top + a.height / 2
  const x1 = b.left + b.width / 2; const y1 = b.top + b.height / 2
  const cx = (x0 + x1) / 2 + (x1 > x0 ? -60 : 60); const cy = Math.min(y0, y1) - 40
  const frames = []
  for (let i = 0; i <= 16; i++) {
    const t = i / 16
    const x = (1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * cx + t * t * x1
    const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * cy + t * t * y1
    frames.push({ transform: `translate(${x}px,${y}px) scale(${1 - t * 0.35})`, offset: t })
  }
  if (props.reveal.key === 'fs') from.closest('g') && from.closest('g').classList.add('gone')
  orbs.value = [{ id: 0 }, { id: 1, echo: true }, { id: 2, echo: true }, { id: 3, echo: true }]
  nextTick(() => {
    const els = orbEls.value || []
    els.forEach((el, i) => {
      const an = el.animate(frames, { duration: 1000, delay: i === 0 ? 0 : i * 60, easing: 'cubic-bezier(.55,0,.35,1)', fill: 'both' })
      anims.push(an)
      if (i === 0) an.onfinish = () => { orbs.value = []; done() }
    })
    if (!els.length) done()
  })
}

function arrive () {
  emit('phase', 'arrived')
  if (props.reveal.seen) emit('seen', props.reveal.seen)
}

function play () {
  const t = revealTiming(props.reveal.key, isReduced.value)
  later(t.light, async () => {
    // The bar makes room first (a new tab waits invisible), then the light lands.
    emit('phase', 'room')
    await nextTick()
    if (isReduced.value) { arrive(); return }
    later(60, () => fly(arrive))
  })
  later(t.fade, () => { fading.value = true })
  // The Moon: the Altar's own first-visit rite is its hero; land straight in it.
  later(t.hero, () => { if (props.reveal.key === 'mo') getStarted(); else stage.value = 'hero' })
}

async function claim () {
  if (claiming.value || claimed.value) return
  claiming.value = true
  const total = savedTotal.value
  try {
    const res = await claimAllQuests(store.session_id)
    if (res && typeof res.balance === 'number') store.querent_carat_balance = res.balance
    // The amount counts down as it moves into the balance.
    if (!isReduced.value && typeof requestAnimationFrame === 'function') {
      const t0 = performance.now()
      await new Promise((resolve) => {
        const tick = () => {
          const p = Math.min(1, (performance.now() - t0) / 1400)
          shownAmount.value = Math.round(total * (1 - p))
          if (p < 1) requestAnimationFrame(tick); else resolve()
        }
        requestAnimationFrame(tick)
      })
    }
    claimed.value = true
    emit('claimed', res)
  } catch (e) { /* the claim waits in the Quest; nothing is lost */ } finally { claiming.value = false }
}

function getStarted () { emit('land', landingFor(props.reveal)) }
function close () { emit('close') }

function onKey (e) { if (e.key === 'Escape' && stage.value === 'hero') close() }

onMounted(() => {
  window.addEventListener('keydown', onKey)
  if (stage.value === 'art') play()
})
onUnmounted(() => {
  timers.forEach(clearTimeout); timers = []
  anims.forEach((a) => { try { a.cancel() } catch (e) { /* */ } }); anims = []
  window.removeEventListener('keydown', onKey)
})
defineExpose({ stage, claim })
</script>

<style scoped>
.qrv-root { position: fixed; inset: 0; z-index: 990; pointer-events: none; }
.qrv, .qrv-over { pointer-events: auto; }
.qrv { position: absolute; left: 0; right: 0; top: 0; bottom: calc(70px + env(safe-area-inset-bottom, 0px)); display: flex; flex-direction: column;
  align-items: center; justify-content: center; background: radial-gradient(110% 75% at 50% 45%, #1d0f3c 0%, #0a0418 72%); animation: qrv-fadein .5s both; }
.qrv.out { animation: qrv-fadeout .45s both; }
.qrv-fig { width: min(380px, 92%); }
.qrv-cap { font: 600 13px Cinzel, serif; letter-spacing: .26em; color: #f0d9a3; text-transform: uppercase; animation: qrv-fadein .9s ease-out var(--cd, 2s) both; margin-top: -6px; }
.qrv-horo { width: min(320px, 86%); margin-top: 6px; border-radius: 16px; padding: 11px 15px 12px; border: 1px solid rgba(240,217,163,.45);
  background: linear-gradient(175deg, #2c1a50, #170b2e); animation: qrv-rise .8s cubic-bezier(.2,.8,.2,1) 3.7s both; }
.qrv-horo p { margin: 4px 0 0; font: 400 17px/1.3 'Cormorant Garamond', Cormorant, serif; color: #efe4ff; }
.qrv-eyebrow { font: 600 10px Cinzel, serif; letter-spacing: .2em; text-transform: uppercase; color: #f0d9a3; }
.qrv-orb { position: fixed; left: 0; top: 0; width: 14px; height: 14px; margin: -7px 0 0 -7px; border-radius: 50%; z-index: 1001; pointer-events: none;
  background: radial-gradient(circle, #fffbe9 0%, #ffe7b5 40%, rgba(240,217,163,0) 72%); box-shadow: 0 0 18px 6px rgba(255,231,181,.55); }
.qrv-orb.echo { width: 9px; height: 9px; margin: -4.5px 0 0 -4.5px; box-shadow: 0 0 10px 3px rgba(255,231,181,.35); }

.qrv-over { position: absolute; left: 0; right: 0; top: 0; bottom: calc(70px + env(safe-area-inset-bottom, 0px)); display: flex; align-items: center;
  justify-content: center; background: rgba(5,2,12,.62); animation: qrv-fadein .35s both; overflow-y: auto; padding: 12px 0; }
/* A hero alone in its room (heroOnly): a solid backdrop, never half see-through
   over the room's own page (e2e 2026-10-30: the Studio's card over the map). */
.qrv-over.solo { background: radial-gradient(110% 75% at 50% 45%, #1d0f3c 0%, #0a0418 72%); }
.qrv-hero { position: relative; width: calc(100% - 36px); max-width: 350px; margin: auto; text-align: center; padding: 20px 22px 18px; border-radius: 24px;
  background: radial-gradient(120% 80% at 50% 0%, #2e1857, #150a2b 75%); border: 1px solid rgba(240,217,163,.55); box-shadow: 0 0 60px rgba(240,217,163,.16);
  animation: qrv-rise .6s cubic-bezier(.2,.8,.2,1) both; color: #efe4ff; }
@media (min-width: 900px) { .qrv-hero { max-width: 440px; padding: 26px 30px 22px; } }
.qrv-x { position: absolute; top: 8px; right: 10px; width: 36px; height: 36px; border: 0; background: none; color: #b9a6d6; font-size: 24px; line-height: 1; cursor: pointer; }
.qrv-x::after { content: ''; position: absolute; inset: -4px; }
.qrv-art { display: flex; justify-content: center; margin-bottom: 8px; }
.qrv-por { width: 96px; height: 96px; object-fit: cover; box-shadow: 0 0 0 2px #f0d9a3; }
.qrv-por.round { border-radius: 50%; }
.qrv-por.square { width: 104px; height: 104px; border-radius: 13px; }
.qrv-title { font: 500 31px/1.08 'Cormorant Garamond', Cormorant, serif; margin: 6px 0 8px; color: #fff; }
.qrv-p { color: #e6dafa; margin: 0 0 9px; font-size: 14px; line-height: 1.5; text-align: left; }
.qrv-p b { color: #f0d9a3; font-weight: 600; }
.qrv-quiet { font: italic 16.5px/1.35 'Cormorant Garamond', Cormorant, serif; color: #b9a6d6; border-top: 1px solid rgba(155,134,201,.2); padding-top: 9px; }
.qrv-btn { display: block; width: 100%; margin-top: 12px; border: 0; border-radius: 14px; padding: 12px 14px; background: linear-gradient(#f3dfa8, #c9a94e);
  color: #22133b; font: 600 13px Cinzel, serif; letter-spacing: .1em; text-transform: uppercase; box-shadow: 0 6px 18px rgba(201,169,78,.25); cursor: pointer; }
.qrv-welcome { margin: 6px 0 2px; border-radius: 18px; padding: 13px 16px; text-align: center; border: 1px solid rgba(240,217,163,.5);
  background: radial-gradient(120% 120% at 50% 0%, rgba(240,217,163,.16), rgba(240,217,163,.03) 70%); animation: qrv-rise .6s cubic-bezier(.2,.8,.2,1) both; }
.qrv-amt { font: 500 34px/1.1 'Cormorant Garamond', Cormorant, serif; color: #ffe7b5; margin: 2px 0 8px; }
.qrv-claim { display: inline-block; width: auto; padding: 9px 26px; margin-top: 0; }
.qrv-goal { margin: 8px 0 0; }
.qrv-goal-bar { height: 4px; border-radius: 99px; background: rgba(155,134,201,.25); overflow: hidden; max-width: 240px; margin: 0 auto 6px; }
.qrv-goal-bar span { display: block; height: 100%; background: linear-gradient(90deg, #c9a94e, #f0d9a3); }
.qrv-goal-line { margin: 0; text-align: center; font: italic 16px 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
.qrv-done { font: italic 17px 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
@keyframes qrv-fadein { from { opacity: 0; } }
@keyframes qrv-fadeout { to { opacity: 0; } }
@keyframes qrv-rise { from { transform: translateY(24px); opacity: 0; } }
/* Reduced motion: everything arrives in place and only fades. */
.rm .qrv-hero, .rm .qrv-welcome, .rm .qrv-horo { animation: qrv-fadein .5s ease-out both; }
.rm .qrv-orb { display: none; }
@media (prefers-reduced-motion: reduce) {
  .qrv-hero, .qrv-welcome, .qrv-horo { animation: qrv-fadein .5s ease-out both; }
  .qrv-orb { display: none; }
}
</style>

<style>
/* The reveal art (v-html): prototype.html's .rv-* motion, ported. */
.qrv svg.fig { width: 100%; height: auto; overflow: visible; display: block; }
.qrv .t { transform-box: fill-box; transform-origin: center; }
.qrv .gone { transition: opacity .25s; opacity: 0; }
@keyframes qrv-draw { from { stroke-dashoffset: 100; } }
@keyframes qrv-fade { from { opacity: 0; } }
@keyframes qrv-out { to { opacity: 0; } }
@keyframes qrv-ignite { 0% { transform: scale(0); opacity: 0; } 12% { transform: scale(.12); opacity: 1; } 65% { transform: scale(1.35); } 100% { transform: scale(1); } }
@keyframes qrv-burst { 0% { transform: scale(.2); opacity: .95; } 100% { transform: scale(3.2); opacity: 0; } }
.qrv-fs .core { animation: qrv-ignite 1.3s cubic-bezier(.2,.9,.3,1.3) .5s both; }
.qrv-fs .flare { animation: qrv-burst 1.5s ease-out 1.1s both; }
.qrv-fs .ray { stroke-dasharray: 100; animation: qrv-draw .8s ease-out 1.3s both; }
.qrv-ri .hz { stroke-dasharray: 100; animation: qrv-draw 1s ease-out .2s both; }
.qrv-ri .up { animation: qrv-riseup 1.4s cubic-bezier(.2,.7,.2,1) both; }
.qrv-ri .up.u1 { animation-delay: .9s; } .qrv-ri .up.u2 { animation-delay: 1.5s; } .qrv-ri .up.u3 { animation-delay: 2.1s; }
@keyframes qrv-riseup { from { transform: translateY(150px); } }
.qrv-ri .lb1 { animation: qrv-fade .6s ease-out 2s both; } .qrv-ri .lb2 { animation: qrv-fade .6s ease-out 2.6s both; } .qrv-ri .lb3 { animation: qrv-fade .6s ease-out 3.2s both; }
.qrv-ri .por { animation: qrv-portin 1.4s ease-out 2.7s both; }
@keyframes qrv-portin { from { opacity: 0; transform: scale(.94); } }
.qrv-ri .name { stroke-dasharray: 420; animation: qrv-write 1.8s ease-in-out 3.7s both, qrv-namefill .7s ease-out 5.3s both; }
@keyframes qrv-write { from { stroke-dashoffset: 420; } }
@keyframes qrv-namefill { from { fill-opacity: 0; } }
.qrv-ve .sa { animation: qrv-driftA 2.4s cubic-bezier(.45,0,.25,1) .3s both; }
.qrv-ve .sb { animation: qrv-driftB 2.4s cubic-bezier(.45,0,.25,1) .3s both; }
@keyframes qrv-driftA { 0% { transform: translate(-138px,34px); } 50% { transform: translate(-62px,-4px); } 100% { transform: none; } }
@keyframes qrv-driftB { 0% { transform: translate(138px,34px); } 50% { transform: translate(62px,-4px); } 100% { transform: none; } }
.qrv-ve .who { animation: qrv-out .4s ease-in 2.3s both; }
.qrv-ve .meet { animation: qrv-burst 1.4s ease-out 2.6s both; }
.qrv-ve .one { animation: qrv-fade .4s ease-out 2.6s both; }
.qrv-ve .glyph { stroke-dasharray: 100; animation: qrv-draw 1.1s ease-out 2.9s both; }
.qrv-ve .glyph2 { stroke-dasharray: 100; animation: qrv-draw .6s ease-out 3.8s both; }
.qrv-ve .nodes { animation: qrv-fade .6s ease-out 3.6s both; }
.qrv-zw .ring { stroke-dasharray: 100; animation: qrv-draw 1.3s ease-out .2s both; }
.qrv-zw .sg { animation: qrv-fade .4s ease-out both; animation-delay: calc(.45s + var(--i) * .08s); }
.qrv-zw .mine { animation: qrv-ignite 1s cubic-bezier(.2,.9,.3,1.3) 1.8s both; }
.qrv-zw .mineb { animation: qrv-burst 1.3s ease-out 1.9s both; }
.qrv-zw .tri { stroke-dasharray: 100; animation: qrv-draw .8s ease-out 2.4s both; }
.qrv-zw .tiny { animation: qrv-twinkle 1.2s ease-out both; animation-delay: var(--d); }
@keyframes qrv-twinkle { 0% { opacity: 0; transform: scale(.2); } 50% { opacity: 1; transform: scale(1.6); } 100% { transform: scale(1); } }
.qrv-mo .shade { transform: translateX(-160px); animation: qrv-wax 3.2s cubic-bezier(.4,0,.3,1) .3s both; }
@keyframes qrv-wax { from { transform: translateX(0); } to { transform: translateX(-160px); } }
.qrv-mo .mglow { animation: qrv-fade .9s ease-out 3.3s both; }
.qrv-su .halfL, .qrv-su .halfR, .qrv-su .sunstars, .qrv-su .rays { transform-box: view-box; }
.qrv-su .sunup { animation: qrv-sunup 2s cubic-bezier(.2,.7,.2,1) .3s both; }
@keyframes qrv-sunup { from { transform: translateY(110px); } }
.qrv-su .split { stroke-dasharray: 100; animation: qrv-draw .5s ease-in 2.2s both; }
.qrv-su .halfL { animation: qrv-partL .9s ease-in 2.75s both; } .qrv-su .halfR { animation: qrv-partR .9s ease-in 2.75s both; }
@keyframes qrv-partL { to { transform: translateX(-46px); opacity: 0; } }
@keyframes qrv-partR { to { transform: translateX(46px); opacity: 0; } }
.qrv-su .rays { animation: qrv-fade .8s ease-out 3.3s both; }
.qrv-su .sunstars { animation: qrv-fade .8s ease-out 3.6s both; }
/* Reduced motion: the art arrives in place; no flight, no ring, no drift. */
.qrv-root.rm .qrv * { animation: none !important; }
.qrv-root.rm .qrv-ve .who, .qrv-root.rm .qrv-su .halfL, .qrv-root.rm .qrv-su .halfR, .qrv-root.rm .qrv-su .split { display: none; }
@media (prefers-reduced-motion: reduce) {
  .qrv * { animation: none !important; }
  .qrv-ve .who, .qrv-su .halfL, .qrv-su .halfR, .qrv-su .split { display: none; }
}
</style>
